import { defaultSpacing } from "./layout/flex.ts";
import { devlog } from "../logic/utils.ts";


// Helper to ensure units are present

export const ensureUnits = (value: string): string => {
  if (!value) return defaultSpacing
  // Check if value already has units
  if (/[a-z%]/i.test(value)) return value
  // Add px if no units present
  return `${value}px`
}

/**
 * Checks if an element has an attribute that is considered "true"
 * An attribute is considered true if:
 * 1. It exists AND
 * 2. Its value is not explicitly "false"
 * 
 * @param element The element to check
 * @param attributeName The name of the attribute to check
 * @returns boolean True if the attribute exists and is not "false"
 */
export const hasEnabledAttribute = (
  element: HTMLElement,
  attributeName: string
): boolean => {
  if (!element.hasAttribute(attributeName)) {
    return false;
  }
  
  const value = element.getAttribute(attributeName)?.toLowerCase();
  return value !== "false";
};

/**
 * Generates an HTML attribute string from matching attributes on the source element
 * @param element The source element to get attributes from
 * @param attributeList Array of attribute names to pass through
 * @param excludeEmpty Whether to exclude attributes with empty values
 * @returns String of HTML attributes in "name='value'" format
 */
export const passAttributes = (
  element: HTMLElement, 
  attributeList: string[], 
  excludeEmpty = false
): string => {
  return attributeList
    .filter(attr => element.hasAttribute(attr))
    .map(attr => {
      const value = element.getAttribute(attr);
      
      // For boolean attributes (those without values)
      if (value === '' || value === null) {
        return excludeEmpty ? '' : attr;
      }
      
      // For attributes with values
      return `${attr}="${value}"`;
    })
    .filter(attr => attr !== '')
    .join(' ');
};

// Define interface for component options
interface ComponentOptions {
  dynamicStyles?: (component: HTMLElement) => string;
  addEventListeners?: () => void;
}

// Helper to create web components with optional dynamic styles
export const defComponent = (tagname: string, template: string | ((el: HTMLElement) => string), styles: string, opts: ComponentOptions = {}) => {
  customElements.define(tagname, class extends HTMLElement {
    private addEventListeners?: () => void = opts.addEventListeners;
    
    constructor() {
      super()
      this.attachShadow({ mode: 'open' })
      this.render()
    }
    
    // Observe these attributes for changes
    static get observedAttributes() {
      return ['left-text', 'center-text', 'right-text']
    }
    
    // Re-render when attributes change
    attributeChangedCallback() {
      this.render()
    }
    
    render() {
      const dynamicStyles = opts.dynamicStyles?.(this) ?? ''
      
      this.shadowRoot.innerHTML = /*html*/`
        <style>
          ${styles}
          ${dynamicStyles}
        </style>
        ${typeof template === 'function' ? template(this) : template}
      `
    }
    
    connectedCallback() {
      this.addEventListeners?.()
    }
  })
}

// Global registry for HMR: maps CSS file paths to elements that use them
if (typeof window !== 'undefined') {
  (window as any).__styleRegistry = (window as any).__styleRegistry || new Map();
}

export function useShadow(host:HTMLElement&{html?:string},initHTML?:string){
  host.attachShadow({ mode: 'open' });
  host.shadowRoot.innerHTML = initHTML??host?.html??`No HTML Provided to useShadow!`
}

export async function useShadowStyle(
  host: HTMLElement & { styleSheet?: CSSStyleSheet; __cssPath?: string },
  styleText: string,
  cssPath: string
) {
  devlog('[useShadowStyle] Called with styleText:', styleText?.substring(0, 50));
  host.styleSheet = new CSSStyleSheet();
  await host.styleSheet.replace(styleText);
  host.shadowRoot.adoptedStyleSheets = [...host.shadowRoot.adoptedStyleSheets, host.styleSheet];
  devlog('[useShadowStyle] Styles applied to', host.tagName);

  // Register element for HMR
  if (typeof window !== 'undefined') {
    host.__cssPath = cssPath;
    const registry = (window as any).__styleRegistry;
    if (!registry.has(cssPath)) {
      registry.set(cssPath, new Set());
    }
    registry.get(cssPath).add(host);
    devlog('[useShadowStyle] Registered element for HMR:', host.tagName, cssPath);
  }
}